'use client';

/**
 * One open connection instead of a timer.
 *
 * `useResource` with `pollMs` asks again every few seconds whether anything
 * changed, and on a quiet afternoon the answer is no a thousand times. This is
 * the other shape: the server holds the picture, pushes a frame when a fact
 * changes, and says nothing in between — so an open tab costs one request and
 * then nothing at all.
 *
 * Same contract as `useResource` deliberately, so a screen reads the same either
 * way: `data` is null until the first frame, `error` is the last failure and
 * never blanks good data, and the two can be true at once (a dropped connection
 * showing the last picture with a note above it).
 *
 * The browser reconnects an `EventSource` by itself, with its own backoff, which
 * is the one piece of this nobody has to write.
 *
 * The first picture comes from an ordinary GET, not from the stream. A stream is
 * one long response, and every proxy between a browser and this API is entitled
 * to hold one until it ends — which for a stream is never. On a developer's
 * machine that is Next's own /api rewrite, and the screen sat on "Connecting…"
 * with a perfectly healthy API behind it. So the page paints from a request that
 * cannot be buffered into silence, and the stream only has to deliver changes:
 * where it works nothing is ever polled, and where it does not the screen is
 * still right, once, and says so.
 */

import { useEffect, useRef, useState } from 'react';
import { useApi, usePollMs, useStreaming } from './api-context';
import { API_URL } from './env';

/** How long a connection may be down before the screen says so. EventSource
 *  reconnects on its own in a second or two, and a note that flashes on every
 *  edge recycle or network blip trains the reader to ignore it — so the word
 *  "dropped" is held back until the drop has actually lasted. */
const DROP_GRACE_MS = 9_000;

export interface Live<T> {
  /** `null` until the first frame arrives; the last frame after that. */
  data: T | null;
  /** The most recent failure, cleared by the next frame. */
  error: string | null;
  /** True while the stream is open. */
  connected: boolean;
}

/** `path` null opens nothing: a hook cannot be called conditionally, a stream can. */
export function useLive<T>(path: string | null, event = 'snapshot'): Live<T> {
  const api = useApi();
  const streaming = useStreaming();
  const pollMs = usePollMs();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);

  // The same address without its last segment: /admin/deploys/stream is the
  // stream, /admin/deploys is the picture, a query string kept on both. One
  // route pair, one convention, so a screen names the stream and gets both.
  const snapshotPath = path?.replace(/\/stream(?=\?|$)/, '') ?? null;

  useEffect(() => {
    if (!snapshotPath) return;
    let live = true;
    api<T>(snapshotPath)
      .then((first) => {
        // Only if the stream has not already said something newer.
        // With nothing streaming, each read is the latest picture, so it replaces.
        if (live) setData((current) => (streaming ? current ?? first : first));
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof Error ? err.message : 'could not load');
      });
    return () => {
      live = false;
    };
  }, [snapshotPath, api, streaming]);

  // A preview has no stream, so a picture that moves is read again on a timer.
  useEffect(() => {
    if (!snapshotPath || streaming || !pollMs) return;
    const timer = setInterval(() => {
      api<T>(snapshotPath)
        .then((fresh) => setData(fresh))
        .catch(() => {});
    }, pollMs);
    return () => clearInterval(timer);
  }, [snapshotPath, api, streaming, pollMs]);

  useEffect(() => {
    // Same origin as every other call: the API answers on this app's own host,
    // so the session cookie rides along and there is no token to attach.
    if (!path) return;
    // Nothing to push in a preview: the first read is the picture, and it is current.
    if (!streaming) {
      setConnected(true);
      return;
    }
    const source = new EventSource(`${API_URL}${path}`);
    // The grace timer that holds back the "dropped" note, and whether the stream
    // has ever opened: the first open is the connection, a later one is a
    // reconnection, and only a reconnection needs the picture pulled afresh.
    let dropTimer: ReturnType<typeof setTimeout> | undefined;
    let opened = false;
    let live = true;

    const clearDrop = () => {
      if (dropTimer) {
        clearTimeout(dropTimer);
        dropTimer = undefined;
      }
    };

    source.addEventListener('open', () => {
      clearDrop();
      setConnected(true);
      setError(null);
      // After a real reconnection the last frame may be stale, so re-pull the
      // picture; the first open already has its GET above and skips this.
      if (opened && snapshotPath) {
        api<T>(snapshotPath)
          .then((fresh) => { if (live) setData(fresh); })
          .catch(() => {});
      }
      opened = true;
    });

    source.addEventListener(event, (message) => {
      clearDrop();
      try {
        setData(JSON.parse((message as MessageEvent<string>).data) as T);
        setError(null);
        setConnected(true);
      } catch {
        setError('the stream sent something this page could not read');
      }
    });

    source.addEventListener('error', () => {
      // Not fatal, and never allowed to blank the picture: the GET above has
      // already painted it, and the browser is reconnecting on its own. The flag
      // flips only if the reconnection does not come back within the grace, so a
      // one-second blip never reaches the screen.
      if (!dropTimer) dropTimer = setTimeout(() => setConnected(false), DROP_GRACE_MS);
    });

    return () => {
      live = false;
      clearDrop();
      source.close();
    };
  }, [path, event, snapshotPath, streaming, api]);

  return { data, error, connected };
}
